import React, {
  ChangeEvent,
  Fragment,
  JSX,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button } from '@/components/atoms/Button/Button';
import { Image } from '@/components/atoms/Image/Image';
import { InputField, InputFieldRef } from '@/components/atoms/InputField/InputField';
import { Typography } from '@/components/atoms/Typography/Typography';
import { isMobileScreen } from '@/utils/sizes';
import { allowPageScroll, blockPageScroll } from '@/utils/storeSetters';
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink';
import { usePathname } from 'next/navigation';
import { TTextMultilanguage } from '@/types/global';
import { useTranslations } from 'next-intl';
import { usePageStore } from '@/store/usePageStore';
import { ITabsAndFilterGroups } from '../../FilteredProjectsGallery/FilteredProjectsGallery.types';
import { useFiltersStore } from '@/store/useFiltersStore';
import { defaultLocale, localizedSlugs } from '@/i18n/routing';
import { capitalizeFirstLetter, capitalizeFirstLettersAllWords } from '@/utils/generic';
import * as Styled from './TopNavAndSearch.styled';


interface TopNavAndSearchProps {
  filtersData: ITabsAndFilterGroups | undefined;
  isPrivatePresentation: boolean;
}

/**  Renders the tab navigation and search input for the filters
 * @param  {TopNavAndSearchProps} props - The component props.
 * @return {JSX.Element}            - TopNavAndSearch component
 */
export const TopNavAndSearch = ({
  filtersData,
  isPrivatePresentation = false,
}: TopNavAndSearchProps): JSX.Element => {
  const isUserLogged = usePageStore((s) => s.isUserLogged);
  const pathname = usePathname();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRefImageSearch = useRef<NodeJS.Timeout | null>(null);
  const refInputTitle = useRef<InputFieldRef>(null);
  const refInputImageSearch = useRef<InputFieldRef>(null);

  const selectedFilters = useFiltersStore((state) => state.selectedFilters);
  const areFiltersOpen = useFiltersStore((state) => state.areFiltersOpen);
  const toggleFilterMenu = useFiltersStore((state) => state.toggleFilterMenu);
  const setSearch = useFiltersStore((state) => state.setSearch);
  const setImageSearch = useFiltersStore((state) => state.setImageSearch);
  const resetSelectedFilters = useFiltersStore((state) => state.resetSelectedFilters);
  const imageSearch = useFiltersStore((state) => state.imageSearch);
  const search = useFiltersStore((state) => state.search);

  const t = useTranslations('work');
  const translations = t.raw('filteredProjectsGallery') as {
    all: string;
    filter: string;
    resetFilters: string;
    searchPlaceholder: string;
    allSubfilters: string;
    searchImagesPlaceholder: string;
  };

  const [shouldPageScroll, setShouldPageScroll] = useState(true);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setShouldPageScroll(window.innerWidth <= 1200);
    }
  }, []);

  useEffect(() => {
    // Update inputs value on navigation when it loads through store
    const inputTitle = refInputTitle.current;
    if (inputTitle && inputTitle.getInputValue() !== search?.value) {
      inputTitle.updateInputValue(search?.value || '');
    }

    const inputImageSearch = refInputImageSearch.current;
    if (inputImageSearch && inputImageSearch.getInputValue() !== imageSearch?.value) {
      inputImageSearch.updateInputValue(imageSearch?.value || '');
    }
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (timeoutRefImageSearch.current) clearTimeout(timeoutRefImageSearch.current);
    };
  }, [imageSearch?.value, search?.value]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (timeoutRefImageSearch.current) clearTimeout(timeoutRefImageSearch.current);
    };
  }, []);

  useEffect(() => {
    if (imageSearch) {
      refInputImageSearch.current?.updateInputValue(imageSearch.value);
    }
  }, [imageSearch]);

  /** Handles the change of the search input with a debounce
   * @param {React.ChangeEvent<HTMLInputElement>} e - The event object.
   */
  const handleChange = (e: ChangeEvent): void => {
    const value = (e.target as HTMLInputElement).value;

    if (value.length === 0) {
      setSearch('');
      return;

    } else if (value.length < 3) {
      return;

    } else {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        setSearch(value);
      }, 500);
    }
  };

  const handleImageSearchChange = (e: ChangeEvent): void => {
    const value = (e.target as HTMLInputElement).value;

    if (value.length === 0) {
      setImageSearch({
        value: '',
        triggerer: 'ui',
      });
      return;

    } else if (value.length < 3) {
      return;

    } else {
      if (timeoutRefImageSearch.current) clearTimeout(timeoutRefImageSearch.current);

      timeoutRefImageSearch.current = setTimeout(() => {
        setImageSearch({
          value: value,
          triggerer: 'ui',
        });
      }, 500);
    }
  }

  /** 
   * Toggles the visibility of the filters on mobile devices
   * and blocks the page scroll if the filters are open
   */
  const toggleFilters = (): void => {
    toggleFilterMenu()

    if (areFiltersOpen && isMobileScreen()) {
      blockPageScroll();
    }
  };

  /** 
   * Closes the mobile filters and allows the page to scroll
   */
  const closeFiltersMobile = (): void => {
    toggleFilterMenu();
    allowPageScroll();
  };

  /** 
   * Character for a separator between filter links 
   */ 
  const Separator = ({ ...props }) => (
    <Typography
      as="span"
      color="secondary"
      text="/"
      className="separator hidden-mobile"
      {...props}
    />
  );

  const resetTitle = () => {
    refInputTitle.current?.updateInputValue('');
    setSearch('');
  }

  const resetImageSearch = () => {
    refInputImageSearch.current?.updateInputValue('');
    setImageSearch({
      value: '',
      triggerer: 'ui',
    });
  };

  const locale = usePageStore.getState().locale;
  const slugAllProj = `${locale !== defaultLocale ? `/${locale}` : ''}/${localizedSlugs.work[locale]}`;

  return (
    <>
      <Styled.ColMainFilters className="hidden-mobile">
        <CustomLink
          text={translations.all}
          slug={slugAllProj}
          color={pathname === slugAllProj ? 'primary' : 'secondary'}
        />
        <Separator />

        {filtersData?.tabs.map(
          (
            tab: {
              text: TTextMultilanguage | string | undefined;
              slug: string;
            },
            i: number
          ) => (
            <Fragment key={`filter-main-${i}`}>
              <CustomLink
                text={
                  typeof tab.text === 'string'
                    ? tab.text.toLocaleLowerCase() === 'consultoría de diseño' 
                      ? 'Consultoría diseño' 
                      : locale === 'es' ? capitalizeFirstLetter(tab.text) : capitalizeFirstLettersAllWords(tab.text)
                    : typeof tab.text === 'object' && tab.text !== null && locale in tab.text
                      ? tab.text[locale] || ''
                      : ''
                }
                key={`btn-filter-${i}`}
                slug={tab.slug}
                lowercase={false}
                color={
                  decodeURIComponent(pathname) === tab.slug
                    ? 'primary'
                    : 'secondary'
                }
                {...{ scroll: shouldPageScroll }}
              />
              {i < filtersData?.tabs.length - 1 && (
                <Separator key={`separator-${i}`} />
              )}
            </Fragment>
          )
        )}
      </Styled.ColMainFilters>
      {!isPrivatePresentation && (
        <Styled.ColMainFilters>
          <Button
            text={translations.filter}
            callbackOnClick={toggleFilters}
            color={!areFiltersOpen ? 'secondary' : (selectedFilters?.filters && selectedFilters?.filters.length === 0) ? 'primary' : 'secondary'}
            className="hidden-mobile"
          />
          <Separator data-is-hidden={(!selectedFilters?.filters || selectedFilters?.filters.length === 0)} />
          <Button
            text={translations.resetFilters}
            color="primary"
            className={`button-reset ${(!selectedFilters?.filters || selectedFilters?.filters.length === 0) ? 'no-filters' : 'has-selected-filters'}`}
            data-is-hidden={(!selectedFilters?.filters || selectedFilters?.filters.length === 0)}
            callbackOnClick={() => resetSelectedFilters()}
          />
          <Button
            callbackOnClick={closeFiltersMobile}
            color="primary"
            className="button-close-filters display-only-mobile"
          >
            <Image
              src="/img/icons/cross.svg"
              alt="close"
              height={25}
              width={25}
              fill={false}
            />
          </Button>
          {
            // Search input only for logged users
            isUserLogged && (
              <Styled.SearchFieldsContainer className='search-fields-container'>
                <Styled.SearchFieldsGrid>
                  <Styled.InputFieldWrapper data-is-filled={!!search?.value}>
                    <InputField
                      type="text"
                      placeholder={translations.searchPlaceholder}
                      callbackOnChange={handleChange}
                      ref={refInputTitle}
                    />
                    <Image
                      src={'/img/icons/magnifying-glass.svg'}
                      alt={'zoom-in'}
                      width={21}
                      height={21}
                      fill={false}
                    />
                  <Button
                    callbackOnClick={resetTitle}
                    color="primary"
                    className="button-reset-search"
                  >
                    <Image
                      src="/img/icons/cross.svg"
                      alt="close"
                      height={25}
                      width={25}
                      fill={false}
                    />
                  </Button>
                  </Styled.InputFieldWrapper>
                  <Styled.InputFieldWrapper data-is-filled={!!imageSearch.value}>
                    <InputField
                      type="text"
                      placeholder={translations.searchImagesPlaceholder}
                      callbackOnChange={handleImageSearchChange}
                      ref={refInputImageSearch}
                    />
                    <Image
                      src={'/img/icons/magnifying-glass.svg'}
                      alt={'zoom-in'}
                      width={21}
                      height={21}
                      fill={false}
                    />
                    <Button
                      callbackOnClick={resetImageSearch}
                      color="primary"
                      className="button-reset-search"
                    >
                      <Image
                        src="/img/icons/cross.svg"
                        alt="close"
                        height={25}
                        width={25}
                        fill={false}
                      />
                    </Button>
                  </Styled.InputFieldWrapper>
                </Styled.SearchFieldsGrid>
              </Styled.SearchFieldsContainer>
            )
          }
        </Styled.ColMainFilters>
      )}
    </>
  );
};
